import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';

import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { Advertiser, CoinReward, ProductReward, PunchCardReceiptToolType, PunchCardStep, PunchCardStrategyType, RewardData, StampReward } from 'src/app/api/api-campaigns.service.generated';
import { PunchCardCampaignService } from '../../punch-card-campaign-edit/punch-card-campaign-edit.service';
import { AdvertiserCampaignRewardEditComponent } from '../../advertiser-campaign-reward-edit/advertiser-campaign-reward-edit.component';
import { CampaignBannerComponent } from '../campaign-banner/campaign-banner.component';
import { PhotoComponent } from 'wecheer-common';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-campaign-flow-item',
  templateUrl: './campaign-flow-item.component.html',
  styleUrls: ['./campaign-flow-item.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FlexLayoutModule,
    CampaignBannerComponent,
    PhotoComponent
  ]
})
export class CampaignFlowItemComponent implements OnInit {
  punchCardCampaignService = inject(PunchCardCampaignService);
  private dialog = inject(MatDialog);


  @Input() advertiserId: string;
  @Input() campaignId: string;
  @Input() flow: PunchCardStep;
  @Input() readonly: boolean = false;
  @Input() allowCoins: boolean = false;
  @Input() allowStamps: boolean = false;
  @Input() allowPoints: boolean = false;
  @Input() group: any;

  @Output() onProductRewardSelected: EventEmitter<ProductReward> = new EventEmitter<ProductReward>();

  grp = new FormGroup({
    title: new FormControl(),
    schemeId: new FormControl(),
    maxStampCount: new FormControl(),
    restartLimit: new FormControl(),
    completeReward: new FormControl(null, [Validators.required]),
    coinReward: new FormControl(),
    stampReward: new FormControl(),
    banner: new FormControl()
  });

  rewardProductsOptions: ProductReward[] = [];
  advertiser: Advertiser;
  receiptToolTypes = PunchCardReceiptToolType;
  ceiptToolTypes = PunchCardReceiptToolType;

  get placeHolderCompleteStep(): string {
    return this.punchCardCampaignService.strategyType === 'Stamps' ? 'Stamps to complete' : 'Points to complete';
  }

  get schemes() {
    return this.punchCardCampaignService.schemList || [];
  }

  get currentScheme() {
    return this.punchCardCampaignService.schemList.filter(s => s.id == this.grp.controls['schemeId'].value)[0]
  }
  get captureToolType() {
    return this.punchCardCampaignService.captureToolType;
  }

  coinRewardOptions = [
    new CoinReward({ coins: 1 }),
    new CoinReward({ coins: 2 }),
    new CoinReward({ coins: 3 }),
    new CoinReward({ coins: 5 }),
    new CoinReward({ coins: 8 }),
    new CoinReward({ coins: 10 }),
    new CoinReward({ coins: 20 }),
    new CoinReward({ coins: 50 }),
    new CoinReward({ coins: 100 })
  ];

  stampRewardOptions = [
    new StampReward({ stamps: 1 }),
    new StampReward({ stamps: 2 }),
    new StampReward({ stamps: 3 }),
    new StampReward({ stamps: 5 }),
    new StampReward({ stamps: 8 }),
    new StampReward({ stamps: 10 }),
    new StampReward({ stamps: 20 }),
    new StampReward({ stamps: 50 }),
    new StampReward({ stamps: 100 })
  ];

  ngOnInit() {
    this.setFormData();
    this.getProductRewards();
    this.listenStrategyChange();
  }



  updateValidator() {
    if (this.punchCardCampaignService.strategyType == PunchCardStrategyType.Stamps) {
      this.grp.get('completeReward').setValidators([Validators.required]);
      this.grp.get('completeReward').updateValueAndValidity();
    } else {
      this.grp.get('completeReward').setValidators(null);
      this.grp.get('completeReward').updateValueAndValidity();
    }
  }
  setFormData() {
    if (!this.flow) {
      return;
    }
    this.grp.patchValue({
      title: this.flow.title,
      schemeId: this.flow.schemaId,
      restartLimit: this.flow.restartLimit,
      maxStampCount: this.flow.maxStampCount,
      banner: this.flow.banner,
      completeReward: this.flow.completeReward?.productReward,
      stampReward: this.flow.completeReward?.stampReward,
      coinReward: this.allowPoints ? this.flow.completeReward?.coinReward?.coins : this.flow.completeReward?.coinReward,
    })
  }

  getFormValue() {
    if (!this.isValid()) {
      return;
    }

    const f = this.grp.value;
    const hasCoins = f.coinReward && f.coinReward.coins;
    const hasInputCoins = f.coinReward;
    const hasStamps = f.stampReward && f.stampReward.stamps;

    const step = new PunchCardStep();
    step.completeReward = new RewardData({
      productReward: new ProductReward(this.grp.get('completeReward').value),
      coinReward: hasCoins ? new CoinReward(f.coinReward) : hasInputCoins ? new CoinReward({ coins: f.coinReward }) : undefined,
      stampReward: hasStamps ? new StampReward(f.stampReward) : undefined,
    })
    return step;
  }

  async getProductRewards() {
    this.rewardProductsOptions = this.punchCardCampaignService.rewardProductsOptions;
    this.advertiser = this.punchCardCampaignService.advertiser;

    this.punchCardCampaignService.onGetProductFinished
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.rewardProductsOptions = this.punchCardCampaignService.rewardProductsOptions;
        this.advertiser = this.punchCardCampaignService.advertiser;
      })
  }

  // complete reward
  async onAddRewardProduct(grp: any) {
    const productReward = await AdvertiserCampaignRewardEditComponent.showDialog(this.dialog, { advertiser: this.advertiser });
    if (!productReward) return;

    productReward.uploadKey = productReward.uploadKey;                     // this flag must create the product reward the the campaing is created

    this.rewardProductsOptions = [
      new ProductReward(productReward),                    // push at 0 index
      ...this.rewardProductsOptions
    ];

    grp.patchValue({
      completeReward: new ProductReward({ ...productReward }),
    });
    this.onProductRewardSelected.emit(new ProductReward({ ...productReward }));
  }

  async onRemoveRewardProduct(grp: any) {
    grp.patchValue({ completeReward: undefined });
    this.onProductRewardSelected.emit(undefined);
  }

  async onRewardSelected(productReward: ProductReward) {
    this.grp.patchValue({
      completeReward: productReward
    });

    this.onProductRewardSelected.emit(productReward);
  }

  hasProductReward(grp: any) {
    const reward = grp.value.completeReward;
    if (!reward) {
      return false;
    }
    return Object.values(reward).some(i => i != undefined && i != null);
  }

  compareRewardData: ((a: ProductReward, b: ProductReward) => boolean) | null = (a: ProductReward, b: ProductReward) => a && b && a.productSlug === b.productSlug;
  compareCoinRewardFn: ((a: CoinReward, b: CoinReward) => boolean) | null = (a: any, b: any) => a && b && a.coins === b.coins;
  compareProductRewardFn: ((a: ProductReward, b: ProductReward) => boolean) | null = (a: ProductReward, b: ProductReward) => a && b && a.productSlug === b.productSlug;
  compareStampRewardFn: ((a: StampReward, b: StampReward) => boolean) | null = (a: any, b: any) => a && b && a.stamps === b.stamps;

  isValid() {
    if (!this.grp.valid) {
      this.forceValidatorsToDisplayErrors();
      return false;
    }
    return this.grp.valid;
  }

  forceValidatorsToDisplayErrors() {
    this.grp.markAllAsTouched();
  }

  private listenStrategyChange() {
    this.punchCardCampaignService.onStrategyChange
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateValidator();
      });
  }
}
